/**
 * src/PdfToolkitPage.js
 *
 * VISINVOKE PDF Toolkit v2 — native VISINVOKE utility module.
 * Hardened, refined, and framework-ready for future VISINVOKE utilities.
 * Mock data only — no backend PDF processing in v1.
 *
 * Framework components (reusable across future utilities):
 * UtilityHero, UtilityActionCard, UtilityStatCard, UtilityHistoryCard,
 * UtilitySettingsCard, UtilityProcessingModal, UtilityResultModal,
 * UtilityEmptyState, UtilityErrorState, UtilitySearchBar, UtilityFilterBar,
 * UtilityRecentFilesPanel, UtilityFavouriteActions, DropZone, OperationSummary
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { glassSurface } from './config/glassTheme';
import { recordProductOpened } from './productActivity';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';

// ── Palette ────────────────────────────────────────────────────────────────
const C = {
  bg:       '#0d1117',
  surface:  '#0f172a',
  border:   'rgba(255,255,255,0.08)',
  borderHi: 'rgba(255,255,255,0.14)',
  text:     '#f1f5f9',
  textMid:  '#94a3b8',
  textFaint:'rgba(255,255,255,0.4)',
  blue:     '#3b82f6',
  blueHi:   '#60a5fa',
  green:    '#4ade80',
  amber:    '#fbbf24',
  red:      '#f87171',
  grad:     'linear-gradient(160deg, #0f172a 0%, #1e3a8a 40%, #1d4ed8 75%, #3b82f6 100%)',
};

// ── Central mock data ──────────────────────────────────────────────────────
const MOCK_HISTORY = [
  { id:1, op:'Merge PDFs',     icon:'🔗', date:'2026-06-12 09:14', files:4,  pages:128, duration:'0m 08s', size:'2.4 MB',  status:'success', output:'merged-document-2026-06-12.pdf' },
  { id:2, op:'Compress PDF',   icon:'🗜️', date:'2026-06-12 08:42', files:1,  pages:42,  duration:'0m 03s', size:'1.1 MB',  status:'success', output:'compressed-document-2026-06-12.pdf', saved:'42%' },
  { id:3, op:'Convert Images', icon:'🖼️', date:'2026-06-11 16:30', files:16, pages:16,  duration:'0m 12s', size:'4.8 MB',  status:'success', output:'images-to-pdf-2026-06-11.pdf' },
  { id:4, op:'Split PDF',      icon:'✂️', date:'2026-06-11 14:12', files:1,  pages:20,  duration:'0m 04s', size:'3 files', status:'success', output:'split-document-2026-06-11' },
  { id:5, op:'Extract Pages',  icon:'📑', date:'2026-06-10 11:05', files:1,  pages:8,   duration:'0m 02s', size:'0.9 MB',  status:'success', output:'extracted-pages-2026-06-10.pdf' },
  { id:6, op:'Rotate PDF',     icon:'🔄', date:'2026-06-09 15:22', files:1,  pages:12,  duration:'0m 01s', size:'1.6 MB',  status:'success', output:'rotated-document-2026-06-09.pdf' },
];

const MOCK_RECENT_FILES = [
  { id:1, name:'Q2-Contracts.pdf',    date:'2026-06-12 09:14', op:'Merge PDFs',  size:'2.4 MB' },
  { id:2, name:'Annual-Report.pdf',   date:'2026-06-12 08:42', op:'Compress PDF',size:'4.2 MB' },
  { id:3, name:'product-photos.zip',  date:'2026-06-11 16:30', op:'Convert Images',size:'18 MB' },
  { id:4, name:'Manual-v3.pdf',       date:'2026-06-11 14:12', op:'Split PDF',   size:'3.1 MB' },
];

const MOCK_OUTPUTS = [
  { id:1, name:'merged-contracts-2026-06-12.pdf',    date:'2026-06-12 09:14', op:'Merge PDFs',  size:'2.4 MB' },
  { id:2, name:'compressed-annual-report.pdf',        date:'2026-06-12 08:42', op:'Compress PDF',size:'1.1 MB', saved:'74%' },
  { id:3, name:'images-to-pdf-2026-06-11.pdf',       date:'2026-06-11 16:30', op:'Convert Images',size:'4.8 MB' },
];

const STATS_WEEK  = { docs:12, pages:342, ops:12, saved:'28 MB' };
const STATS_MONTH = { docs:47, pages:1284, ops:47, saved:'112 MB' };
const STATS_MOST  = 'Merge PDFs';
const STATS_RATE  = '100%';

const DEFAULT_FAVS = ['merge', 'compress'];

// ── Framework: Shared primitives ───────────────────────────────────────────

function Btn({ label, onClick, variant='ghost', size='md', fullWidth, disabled, icon }) {
  const sizes = {
    lg:{ fontSize:16, padding:'14px 28px' },
    md:{ fontSize:14, padding:'10px 22px' },
    sm:{ fontSize:13, padding:'7px 16px'  },
  };
  const vars = {
    primary: glassSurface(C.blue, C.blue, C.blue),
    ghost:   glassSurface('#334155', '#64748B', '#94A3B8'),
    danger:  glassSurface(C.red, C.red, C.red),
    white:   glassSurface(C.blue, C.blue, C.blue),
    subtle:  glassSurface(C.blue, C.blue, C.blue),
    green:   glassSurface(C.green, C.green, C.green),
  };
  return (
    <button onClick={!disabled ? onClick : undefined} disabled={disabled}
      style={{ ...sizes[size], ...vars[variant], borderRadius:9, fontWeight:700,
        cursor:disabled ? 'not-allowed' : 'pointer', whiteSpace:'nowrap',
        width:fullWidth ? '100%' : undefined, opacity:disabled ? 0.5 : 1,
        display:'flex', alignItems:'center', gap:icon ? 8 : 0, justifyContent:'center' }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.filter = 'brightness(1.15)'; }}
      onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)'; }}>
      {icon && <span style={{ fontSize:size === 'lg' ? 20 : 16 }}>{icon}</span>}
      {label}
    </button>
  );
}

function SectionTitle({ children, actionLabel, onAction }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
      <h2 style={{ margin:0, fontSize:20, fontWeight:800, color:C.text, letterSpacing:'-0.2px' }}>{children}</h2>
      {actionLabel && <Btn label={actionLabel} onClick={onAction} size="sm" />}
    </div>
  );
}

// ── Framework: UtilityStatCard ─────────────────────────────────────────────
function UtilityStatCard({ icon, label, value, accent, sub }) {
  return (
    <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:'20px 22px' }}>
      <div style={{ fontSize:22, marginBottom:8 }}>{icon}</div>
      <div style={{ fontSize:12, color:C.textFaint, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:6 }}>{label}</div>
      <div style={{ fontSize:32, fontWeight:900, color:accent || C.text, letterSpacing:'-0.6px', lineHeight:1 }}>{value}</div>
      {sub && <div style={{ fontSize:13, color:C.textFaint, marginTop:4 }}>{sub}</div>}
    </div>
  );
}

// ── Framework: UtilityHero ─────────────────────────────────────────────────
function UtilityHero({ title, subtitle, children }) {
  return (
    <div style={{ background:C.grad, border:`1px solid ${C.borderHi}`, borderRadius:16,
      padding:'32px 40px', boxShadow:'0 8px 32px rgba(0,0,0,0.4)' }}>
      <div style={{ fontSize:12, fontWeight:700, color:'rgba(255,255,255,0.5)',
        textTransform:'uppercase', letterSpacing:'0.12em', marginBottom:12 }}>VISINVOKE PDF Toolkit</div>
      <div style={{ fontSize:32, fontWeight:900, color:C.text, letterSpacing:'-0.6px', lineHeight:1.15, marginBottom:8 }}>{title}</div>
      <div style={{ fontSize:15, color:'rgba(255,255,255,0.65)', marginBottom:children ? 20 : 0 }}>{subtitle}</div>
      {children}
    </div>
  );
}

// ── Framework: UtilityActionCard ───────────────────────────────────────────
function UtilityActionCard({ icon, label, desc, types, onLaunch, isFav, onToggleFav }) {
  return (
    <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:14,
      padding:'24px 22px', display:'flex', flexDirection:'column', gap:14,
      cursor:'pointer', transition:'border-color 0.15s, box-shadow 0.15s', position:'relative' }}
      onClick={onLaunch}
      onMouseEnter={e => { e.currentTarget.style.borderColor = C.blue; e.currentTarget.style.boxShadow = `0 0 0 1px ${C.blue}33`; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.boxShadow = 'none'; }}>
      {/* Favourite star */}
      <button onClick={e => { e.stopPropagation(); onToggleFav(); }}
        style={{ position:'absolute', top:14, right:14, background:'none', border:'none',
          cursor:'pointer', fontSize:18, color:isFav ? C.amber : 'rgba(255,255,255,0.2)',
          lineHeight:1, padding:4 }}
        title={isFav ? 'Remove from favourites' : 'Add to favourites'}>
        ★
      </button>
      <div style={{ fontSize:36, lineHeight:1 }}>{icon}</div>
      <div>
        <div style={{ fontSize:17, fontWeight:800, color:C.text, marginBottom:5 }}>{label}</div>
        <div style={{ fontSize:14, color:C.textMid, lineHeight:1.5, marginBottom:8 }}>{desc}</div>
        <div style={{ fontSize:12, color:C.textFaint, background:'rgba(255,255,255,0.04)',
          borderRadius:5, padding:'3px 9px', display:'inline-block' }}>{types}</div>
      </div>
      <div style={{ marginTop:'auto' }}>
        <Btn label={label} variant="subtle" size="sm" onClick={e => { e.stopPropagation(); onLaunch(); }} />
      </div>
    </div>
  );
}

// ── Framework: UtilityEmptyState ───────────────────────────────────────────
function UtilityEmptyState({ icon, title, body }) {
  return (
    <div style={{ background:'rgba(255,255,255,0.02)', border:`1px dashed ${C.border}`,
      borderRadius:14, padding:'48px 32px', textAlign:'center' }}>
      <div style={{ fontSize:40, marginBottom:14 }}>{icon || '📂'}</div>
      <div style={{ fontSize:17, fontWeight:700, color:C.textMid, marginBottom:8 }}>{title}</div>
      <div style={{ fontSize:14, color:C.textFaint, lineHeight:1.7 }}>{body}</div>
    </div>
  );
}

// ── Framework: UtilityErrorState ───────────────────────────────────────────
function UtilityErrorState({ type }) {
  const errors = {
    unsupported:  { icon:'🚫', title:'Unsupported File Type',   body:'Only PDF files are supported for this operation. Please select a valid PDF.' },
    corrupted:    { icon:'⚠️', title:'File May Be Corrupted',   body:'This file could not be read. It may be damaged. Try re-saving or re-exporting the original.' },
    encrypted:    { icon:'🔒', title:'Encrypted PDF',           body:'This PDF is password-protected. Remove the password in your PDF viewer before uploading.' },
    toolarge:     { icon:'📦', title:'File Too Large',          body:'Files up to 100 MB are supported. Please compress or split your PDF before uploading.' },
    failed:       { icon:'❌', title:'Processing Failed',       body:'Something went wrong during processing. Please try again. If the issue continues, try a different file.' },
    uploadfailed: { icon:'🔌', title:'Upload Failed',           body:'The file could not be uploaded. Check your connection and try again.' },
  };
  const e = errors[type] || errors.failed;
  return (
    <div style={{ background:'rgba(248,113,113,0.06)', border:'1px solid rgba(248,113,113,0.2)',
      borderRadius:12, padding:'24px', display:'flex', gap:16 }}>
      <span style={{ fontSize:28, flexShrink:0 }}>{e.icon}</span>
      <div>
        <div style={{ fontSize:16, fontWeight:700, color:C.red, marginBottom:6 }}>{e.title}</div>
        <div style={{ fontSize:14, color:C.textMid, lineHeight:1.65 }}>{e.body}</div>
      </div>
    </div>
  );
}

// ── Framework: UtilitySearchBar ────────────────────────────────────────────
function UtilitySearchBar({ value, onChange, placeholder }) {
  return (
    <input value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder || 'Search...'}
      style={{ background:'rgba(255,255,255,0.06)', border:`1px solid ${C.border}`,
        borderRadius:9, padding:'9px 16px', fontSize:14, color:C.text,
        outline:'none', width:'100%', boxSizing:'border-box' }} />
  );
}

// ── Framework: UtilityFilterBar ────────────────────────────────────────────
function UtilityFilterBar({ options, active, onChange }) {
  return (
    <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
      {options.map(o => (
        <button key={o.value} onClick={() => onChange(o.value)}
          style={{ padding:'6px 14px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer',
            border:`1px solid ${active === o.value ? C.blue : C.border}`,
            background:active === o.value ? 'rgba(59,130,246,0.18)' : 'rgba(255,255,255,0.05)',
            color:active === o.value ? C.blueHi : C.textMid }}>
          {o.label}{o.count !== undefined ? ` (${o.count})` : ''}
        </button>
      ))}
    </div>
  );
}

// ── Framework: UtilityHistoryCard ──────────────────────────────────────────
function UtilityHistoryCard({ item, expanded, onToggle }) {
  return (
    <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, overflow:'hidden' }}>
      <div style={{ padding:'16px 22px', display:'flex', alignItems:'center', gap:16, flexWrap:'wrap' }}>
        <div style={{ width:38, height:38, borderRadius:10, background:'rgba(59,130,246,0.15)',
          display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>
          {item.icon}
        </div>
        <div style={{ flex:1, minWidth:140 }}>
          <div style={{ fontSize:15, fontWeight:700, color:C.text, marginBottom:2 }}>{item.op}</div>
          <div style={{ fontSize:13, color:C.textFaint }}>{item.date}</div>
        </div>
        <div style={{ display:'flex', gap:22, flexShrink:0, flexWrap:'wrap' }}>
          {[['Files', item.files], ['Pages', item.pages], ['Size', item.size], ['Time', item.duration]].map(([l,v]) => (
            <div key={l} style={{ textAlign:'right' }}>
              <div style={{ fontSize:11, color:C.textFaint, marginBottom:2 }}>{l}</div>
              <div style={{ fontSize:17, fontWeight:800, color:C.text }}>{v}</div>
            </div>
          ))}
        </div>
        <span style={{ fontSize:11, fontWeight:700, color:C.green, background:'rgba(74,222,128,0.1)',
          border:'1px solid rgba(74,222,128,0.25)', borderRadius:5, padding:'3px 10px',
          textTransform:'uppercase', letterSpacing:'0.07em' }}>{item.status}</span>
        <div style={{ display:'flex', gap:8 }}>
          <Btn label={expanded ? 'Less' : 'Details'} size="sm" variant="subtle" onClick={onToggle} />
          <Btn label="Reuse" size="sm" onClick={() => {}} />
        </div>
      </div>
      {expanded && (
        <div style={{ padding:'14px 22px 18px', borderTop:`1px solid ${C.border}`,
          background:'rgba(255,255,255,0.02)' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:12 }}>
            {[
              { label:'Output File', value:item.output },
              { label:'Operation',   value:item.op     },
              { label:'Completed',   value:item.date   },
            ].map(r => (
              <div key={r.label}>
                <div style={{ fontSize:12, color:C.textFaint, marginBottom:3 }}>{r.label}</div>
                <div style={{ fontSize:14, color:C.text, fontWeight:600, wordBreak:'break-all' }}>{r.value}</div>
              </div>
            ))}
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <Btn label="Download Output" variant="green" size="sm" icon="⬇️" onClick={() => {}} />
            <Btn label="Run Same Operation" size="sm" onClick={() => {}} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Framework: UtilitySettingsCard ─────────────────────────────────────────
function UtilitySettingsCard({ icon, label, desc }) {
  return (
    <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:14,
      padding:'22px 26px', display:'flex', flexDirection:'column', gap:12 }}>
      <div style={{ display:'flex', alignItems:'center', gap:12 }}>
        <span style={{ fontSize:24 }}>{icon}</span>
        <div style={{ fontSize:17, fontWeight:800, color:C.text }}>{label}</div>
      </div>
      <div style={{ fontSize:15, color:C.textMid, lineHeight:1.65, flex:1 }}>{desc}</div>
      <div><Btn label={`Configure ${label}`} size="sm" onClick={() => {}} /></div>
    </div>
  );
}

// ── Framework: UtilityRecentFilesPanel ────────────────────────────────────
function UtilityRecentFilesPanel({ files, onReuse }) {
  if (!files || files.length === 0) {
    return <UtilityEmptyState icon="📂" title="No Recent Files" body="Files you process will appear here for quick reuse." />;
  }
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
      {files.map(f => (
        <div key={f.id} style={{ background:C.surface, border:`1px solid ${C.border}`,
          borderRadius:12, padding:'14px 20px', display:'flex', alignItems:'center', gap:14, flexWrap:'wrap' }}>
          <span style={{ fontSize:22, flexShrink:0 }}>📄</span>
          <div style={{ flex:1, minWidth:120 }}>
            <div style={{ fontSize:15, fontWeight:700, color:C.text, marginBottom:2 }}>{f.name}</div>
            <div style={{ fontSize:13, color:C.textFaint }}>{f.op} · {f.date} · {f.size}</div>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <Btn label="Run Again" size="sm" variant="subtle" onClick={() => onReuse(f)} />
            <Btn label="Remove" size="sm" variant="danger" onClick={() => {}} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Framework: UtilityFavouriteActions ────────────────────────────────────
function UtilityFavouriteActions({ favs, tools, onSelect }) {
  const favTools = tools.filter(t => favs.includes(t.id));
  if (favTools.length === 0) return null;
  return (
    <div>
      <div style={{ fontSize:13, fontWeight:700, color:C.amber, textTransform:'uppercase',
        letterSpacing:'0.1em', marginBottom:12, display:'flex', alignItems:'center', gap:8 }}>
        <span>★</span> Favourite Tools
      </div>
      <div style={{ display:'grid', gridTemplateColumns:`repeat(${favTools.length}, 1fr)`, gap:12 }}>
        {favTools.map(t => (
          <button key={t.id} onClick={() => onSelect(t.tab)}
            style={{ background:'rgba(251,191,36,0.06)', border:'1px solid rgba(251,191,36,0.2)',
              borderRadius:12, padding:'16px 18px', cursor:'pointer', textAlign:'left',
              display:'flex', alignItems:'center', gap:12 }}>
            <span style={{ fontSize:24 }}>{t.icon}</span>
            <div>
              <div style={{ fontSize:15, fontWeight:700, color:C.text }}>{t.label}</div>
              <div style={{ fontSize:12, color:C.textFaint }}>{t.types}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Framework: OperationSummary ────────────────────────────────────────────
function OperationSummary({ op, files, pages, inputSize, outputName, estOutputSize, onProceed, onCancel, disabled, proceedLabel }) {
  return (
    <div style={{ background:'rgba(59,130,246,0.06)', border:`1px solid rgba(59,130,246,0.2)`,
      borderRadius:14, padding:'24px 28px' }}>
      <div style={{ fontSize:15, fontWeight:800, color:C.text, marginBottom:16 }}>Ready to Process</div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:12, marginBottom:20 }}>
        {[
          { label:'Operation',    value:op          },
          { label:'Files',        value:files       },
          { label:'Pages',        value:pages || '—'},
          { label:'Input Size',   value:inputSize   },
          { label:'Output File',  value:outputName  },
          { label:'Est. Output',  value:estOutputSize || '—' },
        ].map(s => (
          <div key={s.label} style={{ background:'rgba(255,255,255,0.04)', borderRadius:8, padding:'10px 14px' }}>
            <div style={{ fontSize:11, color:C.textFaint, marginBottom:3 }}>{s.label}</div>
            <div style={{ fontSize:14, fontWeight:700, color:C.text, wordBreak:'break-all' }}>{s.value}</div>
          </div>
        ))}
      </div>
      <div style={{ display:'flex', gap:10 }}>
        <Btn label={proceedLabel || 'Process Now'} variant="white" size="lg" onClick={onProceed} disabled={disabled} />
        <Btn label="Cancel" onClick={onCancel} />
      </div>
    </div>
  );
}

// ── Framework: UtilityProcessingModal ─────────────────────────────────────
const PROCESS_STAGES = ['Preparing Files', 'Checking Document', 'Applying Changes', 'Creating Output', 'Finalising', 'Complete'];

function UtilityProcessingModal({ operation, onComplete, onClose }) {
  const [stage, setStage]   = useState(0);
  const [done, setDone]     = useState(false);
  const [result, setResult] = useState(null);
  const [cancelled, setCancelled] = useState(false);

  React.useEffect(() => {
    if (cancelled) return;
    const t = setInterval(() => {
      setStage(s => {
        if (s >= PROCESS_STAGES.length - 2) {
          clearInterval(t);
          setTimeout(() => {
            setDone(true);
            setResult({
              filename: operation.outputName,
              size:     operation.estSize || '1.8 MB',
              pages:    operation.pages   || 12,
              duration: '0m 04s',
            });
          }, 400);
          return PROCESS_STAGES.length - 1;
        }
        return s + 1;
      });
    }, 500);
    return () => clearInterval(t);
  }, [operation, cancelled]);

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'#1e293b', border:`1px solid ${C.borderHi}`, borderRadius:16,
        padding:'40px', width:'100%', maxWidth:480, boxShadow:'0 24px 64px rgba(0,0,0,0.6)' }}>

        {cancelled ? (
          <div style={{ textAlign:'center', padding:'8px 0 16px' }}>
            <div style={{ fontSize:40, marginBottom:12 }}>⏹️</div>
            <div style={{ fontSize:20, fontWeight:800, color:C.text, marginBottom:8 }}>Operation Cancelled</div>
            <div style={{ fontSize:14, color:C.textFaint, marginBottom:24 }}>No files were modified.</div>
            <Btn label="Close" variant="primary" onClick={onClose} fullWidth />
          </div>
        ) : !done ? (
          <>
            <div style={{ textAlign:'center', marginBottom:28 }}>
              <div style={{ fontSize:38, marginBottom:10 }}>⚙️</div>
              <div style={{ fontSize:20, fontWeight:900, color:C.text, marginBottom:4 }}>{operation.label}</div>
              <div style={{ fontSize:14, color:C.textMid }}>Processing your document...</div>
            </div>
            {/* Progress bar */}
            <div style={{ height:4, background:'rgba(255,255,255,0.08)', borderRadius:2, marginBottom:24, overflow:'hidden' }}>
              <div style={{ height:'100%', width:`${((stage + 1) / PROCESS_STAGES.length) * 100}%`,
                background:C.blue, borderRadius:2, transition:'width 0.5s ease' }} />
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:24 }}>
              {PROCESS_STAGES.map((s, i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <span style={{ fontSize:15, width:22, textAlign:'center' }}>
                    {i < stage ? '✅' : i === stage ? '⏳' : '○'}
                  </span>
                  <span style={{ fontSize:14, color:i <= stage ? C.text : C.textFaint,
                    fontWeight:i === stage ? 700 : 400 }}>{s}</span>
                  {i === stage && <span style={{ fontSize:12, color:C.textFaint, marginLeft:'auto' }}>In progress</span>}
                </div>
              ))}
            </div>
            <Btn label="Cancel Operation" variant="danger" onClick={() => setCancelled(true)} fullWidth />
          </>
        ) : (
          // ── Framework: UtilityResultModal ─────────────────────────────
          <div style={{ textAlign:'center', padding:'4px 0 8px' }}>
            <div style={{ fontSize:48, marginBottom:14 }}>✅</div>
            <div style={{ fontSize:22, fontWeight:900, color:C.text, marginBottom:6 }}>Operation Complete</div>
            <div style={{ fontSize:15, color:C.textMid, marginBottom:24 }}>{operation.label}</div>
            <div style={{ background:'rgba(255,255,255,0.04)', borderRadius:10, padding:'16px', marginBottom:24, textAlign:'left' }}>
              {[
                { label:'Output File',  value:result.filename },
                { label:'Output Size',  value:result.size     },
                { label:'Pages',        value:result.pages    },
                { label:'Time Taken',   value:result.duration },
              ].map(r => (
                <div key={r.label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                  padding:'8px 0', borderBottom:`1px solid ${C.border}` }}>
                  <span style={{ fontSize:14, color:C.textFaint }}>{r.label}</span>
                  <span style={{ fontSize:14, color:C.text, fontWeight:700, wordBreak:'break-all', maxWidth:'60%', textAlign:'right' }}>{r.value}</span>
                </div>
              ))}
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              <Btn label="Download File" variant="white" size="lg" fullWidth icon="⬇️" onClick={onClose} />
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <Btn label="Reuse Output" variant="subtle" onClick={onClose} />
                <Btn label="Start Another" onClick={onComplete} />
              </div>
              <Btn label="View Activity History" onClick={() => { onClose(); }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Drop zone ──────────────────────────────────────────────────────────────
function DropZone({ accept, multiple, onFiles, files, label, sublabel }) {
  const [dragging, setDragging] = useState(false);
  const [error, setError]       = useState(null);

  const validate = fileList => {
    const accepted = accept ? accept.split(',').map(a => a.trim()) : [];
    for (const f of fileList) {
      const ext = '.' + f.name.split('.').pop().toLowerCase();
      const mime = f.type;
      const ok = accepted.some(a => a === ext || a === mime || a === 'image/*' && mime.startsWith('image/'));
      if (!ok) { setError('unsupported'); return false; }
      if (f.size > 100 * 1024 * 1024) { setError('toolarge'); return false; }
    }
    setError(null);
    return true;
  };

  const handleDrop = e => {
    e.preventDefault(); setDragging(false);
    const dropped = Array.from(e.dataTransfer.files);
    if (validate(dropped)) onFiles(multiple ? dropped : [dropped[0]]);
  };

  const handleChange = e => {
    const selected = Array.from(e.target.files);
    if (validate(selected)) onFiles(multiple ? selected : [selected[0]]);
  };

  if (error) {
    return (
      <div>
        <UtilityErrorState type={error} />
        <div style={{ marginTop:12 }}>
          <Btn label="Try Again" onClick={() => setError(null)} />
        </div>
      </div>
    );
  }

  if (files && files.length > 0) {
    return (
      <div style={{ border:`2px solid ${C.green}`, borderRadius:12,
        padding:'20px 24px', background:'rgba(74,222,128,0.05)' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
          <span style={{ fontSize:15, fontWeight:700, color:C.green }}>
            {files.length} file{files.length > 1 ? 's' : ''} selected
          </span>
          <Btn label="Change Files" size="sm" onClick={() => onFiles([])} />
        </div>
        {files.map((f, i) => (
          <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'8px 0',
            borderTop:i > 0 ? `1px solid ${C.border}` : 'none' }}>
            <span style={{ fontSize:18 }}>📄</span>
            <span style={{ fontSize:14, color:C.text, flex:1, minWidth:0, overflow:'hidden',
              textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.name}</span>
            <span style={{ fontSize:13, color:C.textFaint, flexShrink:0 }}>{(f.size/1024/1024).toFixed(2)} MB</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      style={{ border:`2px dashed ${dragging ? C.blue : C.border}`, borderRadius:12,
        padding:'48px 32px', textAlign:'center', cursor:'pointer',
        background:dragging ? 'rgba(59,130,246,0.06)' : 'rgba(255,255,255,0.02)',
        transition:'all 0.15s ease' }}>
      <label style={{ cursor:'pointer', display:'block' }}>
        <input type="file" accept={accept} multiple={multiple} style={{ display:'none' }} onChange={handleChange} />
        <div style={{ fontSize:40, marginBottom:12 }}>{dragging ? '📂' : '📁'}</div>
        <div style={{ fontSize:16, fontWeight:700, color:C.text, marginBottom:6 }}>{label || 'Drop files here or click to browse'}</div>
        <div style={{ fontSize:14, color:C.textFaint }}>{sublabel || accept}</div>
        {dragging && <div style={{ fontSize:14, color:C.blueHi, marginTop:8, fontWeight:600 }}>Release to upload</div>}
      </label>
    </div>
  );
}

// ── Tool workspace hero card ───────────────────────────────────────────────
function ToolHero({ icon, title, desc }) {
  return (
    <div style={{ background:C.grad, border:`1px solid ${C.borderHi}`, borderRadius:14, padding:'26px 30px' }}>
      <div style={{ fontSize:30, marginBottom:8 }}>{icon}</div>
      <div style={{ fontSize:22, fontWeight:900, color:C.text, marginBottom:4 }}>{title}</div>
      <div style={{ fontSize:15, color:'rgba(255,255,255,0.65)' }}>{desc}</div>
    </div>
  );
}

// ── Radio option ───────────────────────────────────────────────────────────
function RadioOption({ value, current, onChange, label, desc, badge }) {
  const active = current === value;
  return (
    <button onClick={() => onChange(value)}
      style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
        padding:'13px 16px', borderRadius:10, cursor:'pointer', textAlign:'left', width:'100%',
        border:`1px solid ${active ? C.blue : C.border}`,
        background:active ? 'rgba(59,130,246,0.12)' : 'rgba(255,255,255,0.04)' }}>
      <div style={{ display:'flex', alignItems:'center', gap:12 }}>
        <div style={{ width:17, height:17, borderRadius:'50%', border:`2px solid ${active ? C.blue : C.textFaint}`,
          display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          {active && <div style={{ width:7, height:7, borderRadius:'50%', background:C.blue }} />}
        </div>
        <div>
          <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{label}</div>
          {desc && <div style={{ fontSize:12, color:C.textFaint }}>{desc}</div>}
        </div>
      </div>
      {badge && (
        <span style={{ fontSize:13, fontWeight:700, color:C.green, background:'rgba(74,222,128,0.1)',
          border:'1px solid rgba(74,222,128,0.25)', borderRadius:6, padding:'2px 10px', flexShrink:0 }}>
          {badge}
        </span>
      )}
    </button>
  );
}

function InputField({ label, value, onChange, placeholder, hint }) {
  return (
    <div>
      <div style={{ fontSize:14, color:C.textMid, marginBottom:7, fontWeight:600 }}>{label}</div>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ background:'rgba(255,255,255,0.06)', border:`1px solid ${C.border}`,
          borderRadius:9, padding:'10px 16px', fontSize:14, color:C.text,
          outline:'none', width:'100%', boxSizing:'border-box' }} />
      {hint && <div style={{ fontSize:12, color:C.textFaint, marginTop:5 }}>{hint}</div>}
    </div>
  );
}

// ── Tool workspaces ────────────────────────────────────────────────────────
const today = () => new Date().toISOString().slice(0, 10);

function MergePDFs({ onProcess }) {
  const [files, setFiles]     = useState([]);
  const [outName, setOutName] = useState(`merged-document-${today()}.pdf`);
  const canMerge = files.length >= 2;
  const totalSize = files.reduce((a, f) => a + f.size, 0);

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:22 }}>
      <ToolHero icon="🔗" title="Merge PDFs" desc="Combine multiple PDF files into a single document. Drag files into any order before merging." />
      <DropZone accept=".pdf,application/pdf" multiple onFiles={setFiles} files={files}
        label="Drop PDF files here or click to browse"
        sublabel="Select two or more PDF files" />
      {files.length > 0 && files.length < 2 && (
        <div style={{ background:'rgba(251,191,36,0.08)', border:'1px solid rgba(251,191,36,0.2)',
          borderRadius:10, padding:'12px 16px', fontSize:14, color:C.amber }}>
          Add at least one more file to merge.
        </div>
      )}
      <InputField label="Output Filename" value={outName} onChange={setOutName} />
      {canMerge && (
        <OperationSummary
          op="Merge PDFs"
          files={`${files.length} PDF files`}
          pages="—"
          inputSize={`${(totalSize/1024/1024).toFixed(1)} MB total`}
          outputName={outName}
          estOutputSize={`~${(totalSize/1024/1024).toFixed(1)} MB`}
          onProceed={() => onProcess({ label:'Merge PDFs', outputName:outName, pages:files.length*10, estSize:`${(totalSize/1024/1024).toFixed(1)} MB` })}
          onCancel={() => setFiles([])}
          proceedLabel="Merge PDFs"
        />
      )}
    </div>
  );
}

function SplitPDF({ onProcess }) {
  const [files, setFiles] = useState([]);
  const [mode, setMode]   = useState('every');
  const [range, setRange] = useState('');
  const [outName, setOutName] = useState(`split-document-${today()}`);
  const hasPDF = files.length > 0;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:22 }}>
      <ToolHero icon="✂️" title="Split PDF" desc="Divide a PDF into multiple separate files by page, range, or custom groups." />
      <DropZone accept=".pdf,application/pdf" multiple={false} onFiles={setFiles} files={files}
        label="Drop a PDF file here or click to browse" sublabel=".pdf files only" />
      {hasPDF && (
        <>
          <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:'22px' }}>
            <div style={{ fontSize:15, fontWeight:700, color:C.textMid, marginBottom:12 }}>Split Method</div>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              <RadioOption value="every"  current={mode} onChange={setMode} label="Every Page"    desc="One PDF per page" />
              <RadioOption value="range"  current={mode} onChange={setMode} label="Page Range"    desc="Split at specific page numbers" />
              <RadioOption value="custom" current={mode} onChange={setMode} label="Custom Groups" desc="Define your own page groupings" />
            </div>
            {(mode === 'range' || mode === 'custom') && (
              <div style={{ marginTop:16 }}>
                <InputField label="Page Selection" value={range} onChange={setRange}
                  placeholder="e.g. 1-5, 8, 10-20"
                  hint="Separate page numbers or ranges with commas." />
              </div>
            )}
          </div>
          <InputField label="Output Filename Base" value={outName} onChange={setOutName} hint="Each output file will be numbered automatically." />
          <OperationSummary op="Split PDF" files="1 PDF file" pages="—" inputSize={`${(files[0].size/1024/1024).toFixed(2)} MB`}
            outputName={outName} estOutputSize="Multiple files"
            onProceed={() => onProcess({ label:'Split PDF', outputName:outName, pages:20, estSize:'3 files' })}
            onCancel={() => setFiles([])} proceedLabel="Split PDF" />
        </>
      )}
    </div>
  );
}

function CompressPDF({ onProcess }) {
  const [files, setFiles]     = useState([]);
  const [profile, setProfile] = useState('balanced');
  const [outName, setOutName] = useState(`compressed-document-${today()}.pdf`);
  const hasPDF = files.length > 0;
  const PROFILES = [
    { value:'quality',  label:'Maximum Quality',     desc:'Smallest reduction, best visual quality', est:'10–20%' },
    { value:'balanced', label:'Balanced',             desc:'Good quality with meaningful size reduction', est:'30–50%' },
    { value:'maximum',  label:'Maximum Compression', desc:'Largest reduction, some quality loss', est:'50–75%' },
  ];
  const sel = PROFILES.find(p => p.value === profile);
  const origMB = hasPDF ? (files[0].size / 1024 / 1024).toFixed(2) : null;
  const estMidPct = hasPDF ? parseInt(sel.est) / 100 : 0;
  const estNew = origMB ? (origMB * (1 - estMidPct)).toFixed(2) : null;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:22 }}>
      <ToolHero icon="🗜️" title="Compress PDF" desc="Reduce PDF file size while preserving document quality." />
      <DropZone accept=".pdf,application/pdf" multiple={false} onFiles={setFiles} files={files}
        label="Drop a PDF file here or click to browse" sublabel=".pdf files only" />
      {hasPDF && (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14 }}>
            {[
              { label:'Original Size',     value:`${origMB} MB`,   accent:C.textMid },
              { label:'Estimated New Size',value:`~${estNew} MB`,  accent:C.green   },
              { label:'Estimated Savings', value:sel.est,          accent:C.amber   },
            ].map(s => (
              <UtilityStatCard key={s.label} label={s.label} value={s.value} accent={s.accent} />
            ))}
          </div>
          <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:'22px' }}>
            <div style={{ fontSize:15, fontWeight:700, color:C.textMid, marginBottom:12 }}>Compression Profile</div>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {PROFILES.map(p => (
                <RadioOption key={p.value} value={p.value} current={profile} onChange={setProfile}
                  label={p.label} desc={p.desc} badge={`~${p.est} saved`} />
              ))}
            </div>
          </div>
          <InputField label="Output Filename" value={outName} onChange={setOutName} />
          <OperationSummary op="Compress PDF" files="1 PDF file" pages="—" inputSize={`${origMB} MB`}
            outputName={outName} estOutputSize={`~${estNew} MB`}
            onProceed={() => onProcess({ label:'Compress PDF', outputName:outName, pages:42, estSize:`${estNew} MB` })}
            onCancel={() => setFiles([])} proceedLabel="Compress PDF" />
        </>
      )}
    </div>
  );
}

function RotatePDF({ onProcess }) {
  const [files, setFiles] = useState([]);
  const [scope, setScope] = useState('all');
  const [angle, setAngle] = useState('90');
  const [pages, setPages] = useState('');
  const [outName, setOutName] = useState(`rotated-document-${today()}.pdf`);
  const hasPDF = files.length > 0;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:22 }}>
      <ToolHero icon="🔄" title="Rotate PDF" desc="Rotate all or selected pages in a PDF document." />
      <DropZone accept=".pdf,application/pdf" multiple={false} onFiles={setFiles} files={files}
        label="Drop a PDF file here or click to browse" sublabel=".pdf files only" />
      {hasPDF && (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:'20px' }}>
              <div style={{ fontSize:14, fontWeight:700, color:C.textMid, marginBottom:10 }}>Scope</div>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                <RadioOption value="all"      current={scope} onChange={setScope} label="All Pages"      />
                <RadioOption value="selected" current={scope} onChange={setScope} label="Selected Pages" />
              </div>
              {scope === 'selected' && (
                <div style={{ marginTop:14 }}>
                  <InputField label="Pages" value={pages} onChange={setPages} placeholder="e.g. 1, 3-5, 8" />
                </div>
              )}
            </div>
            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:'20px' }}>
              <div style={{ fontSize:14, fontWeight:700, color:C.textMid, marginBottom:10 }}>Rotation Angle</div>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                <RadioOption value="90"  current={angle} onChange={setAngle} label="90° Clockwise"   />
                <RadioOption value="180" current={angle} onChange={setAngle} label="180° (Flip)"     />
                <RadioOption value="270" current={angle} onChange={setAngle} label="270° Clockwise"  />
              </div>
            </div>
          </div>
          <InputField label="Output Filename" value={outName} onChange={setOutName} />
          <OperationSummary op="Rotate PDF" files="1 PDF file" pages="—" inputSize={`${(files[0].size/1024/1024).toFixed(2)} MB`}
            outputName={outName}
            onProceed={() => onProcess({ label:'Rotate PDF', outputName:outName, pages:12, estSize:'1.6 MB' })}
            onCancel={() => setFiles([])} proceedLabel="Rotate PDF" />
        </>
      )}
    </div>
  );
}

function ExtractPages({ onProcess }) {
  const [files, setFiles]     = useState([]);
  const [selection, setSel]   = useState('');
  const [outName, setOutName] = useState(`extracted-pages-${today()}.pdf`);
  const hasPDF = files.length > 0;
  const canExtract = hasPDF && selection.trim().length > 0;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:22 }}>
      <ToolHero icon="📑" title="Extract Pages" desc="Extract specific pages from a PDF into a new document." />
      <DropZone accept=".pdf,application/pdf" multiple={false} onFiles={setFiles} files={files}
        label="Drop a PDF file here or click to browse" sublabel=".pdf files only" />
      {hasPDF && (
        <>
          <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:'22px' }}>
            <InputField label="Pages to Extract" value={selection} onChange={setSel}
              placeholder="e.g. 1-5, 8, 10-20"
              hint="Separate page numbers and ranges with commas. Example: 1-5, 8, 10-20" />
          </div>
          <InputField label="Output Filename" value={outName} onChange={setOutName} />
          {canExtract && (
            <OperationSummary op="Extract Pages" files="1 PDF file" pages={selection}
              inputSize={`${(files[0].size/1024/1024).toFixed(2)} MB`}
              outputName={outName}
              onProceed={() => onProcess({ label:'Extract Pages', outputName:outName, pages:parseInt(selection)||8, estSize:'0.9 MB' })}
              onCancel={() => setFiles([])} proceedLabel="Extract Pages" />
          )}
          {hasPDF && !canExtract && (
            <div style={{ background:'rgba(251,191,36,0.08)', border:'1px solid rgba(251,191,36,0.2)',
              borderRadius:10, padding:'12px 16px', fontSize:14, color:C.amber }}>
              Enter the page numbers or ranges you want to extract.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ConvertImages({ onProcess }) {
  const [files, setFiles]     = useState([]);
  const [pageSize, setPage]   = useState('a4');
  const [orient, setOrient]   = useState('portrait');
  const [merge, setMerge]     = useState('single');
  const [outName, setOutName] = useState(`images-to-pdf-${today()}.pdf`);
  const hasFiles = files.length > 0;
  const totalSize = files.reduce((a, f) => a + f.size, 0);

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:22 }}>
      <ToolHero icon="🖼️" title="Convert Images to PDF" desc="Convert JPG, PNG, WEBP, or BMP images into a PDF document." />
      <DropZone accept=".jpg,.jpeg,.png,.webp,.bmp,image/*" multiple onFiles={setFiles} files={files}
        label="Drop image files here or click to browse"
        sublabel="JPG, PNG, WEBP, and BMP supported" />
      {hasFiles && (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14 }}>
            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:'18px' }}>
              <div style={{ fontSize:14, fontWeight:700, color:C.textMid, marginBottom:10 }}>Output Type</div>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                <RadioOption value="single"   current={merge} onChange={setMerge} label="Single PDF"        desc="All images in one PDF" />
                <RadioOption value="separate" current={merge} onChange={setMerge} label="One PDF per Image" desc="Individual PDFs" />
              </div>
            </div>
            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:'18px' }}>
              <div style={{ fontSize:14, fontWeight:700, color:C.textMid, marginBottom:10 }}>Page Size</div>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                <RadioOption value="a4"     current={pageSize} onChange={setPage} label="A4"     desc="210 × 297 mm" />
                <RadioOption value="letter" current={pageSize} onChange={setPage} label="Letter" desc="8.5 × 11 in" />
              </div>
            </div>
            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:'18px' }}>
              <div style={{ fontSize:14, fontWeight:700, color:C.textMid, marginBottom:10 }}>Orientation</div>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                <RadioOption value="portrait"  current={orient} onChange={setOrient} label="Portrait"  />
                <RadioOption value="landscape" current={orient} onChange={setOrient} label="Landscape" />
              </div>
            </div>
          </div>
          <InputField label="Output Filename" value={outName} onChange={setOutName} />
          <OperationSummary op="Convert Images to PDF"
            files={`${files.length} image${files.length > 1 ? 's' : ''}`}
            pages={files.length}
            inputSize={`${(totalSize/1024/1024).toFixed(1)} MB total`}
            outputName={outName}
            onProceed={() => onProcess({ label:'Convert Images to PDF', outputName:outName, pages:files.length, estSize:`${(totalSize/1024/1024).toFixed(1)} MB` })}
            onCancel={() => setFiles([])} proceedLabel="Convert Images to PDF" />
        </>
      )}
    </div>
  );
}

// ── Activity History ───────────────────────────────────────────────────────
const HIST_FILTERS = [
  {value:'all',label:'All'},{value:'Merge',label:'Merge'},{value:'Split',label:'Split'},
  {value:'Compress',label:'Compress'},{value:'Rotate',label:'Rotate'},
  {value:'Extract',label:'Extract'},{value:'Convert',label:'Convert'},
];
const QUICK_FILTERS = [
  {value:'all',label:'All Time'},{value:'today',label:'Today'},
  {value:'week',label:'This Week'},{value:'month',label:'This Month'},
];

function ActivityHistory() {
  const [opFilter, setOpFilter]     = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const [search, setSearch]         = useState('');
  const [expanded, setExpanded]     = useState(null);

  const filtered = useMemo(() => MOCK_HISTORY.filter(h => {
    const matchOp   = opFilter === 'all' || h.op.startsWith(opFilter);
    const matchSearch = !search || h.op.toLowerCase().includes(search.toLowerCase()) || h.date.includes(search);
    return matchOp && matchSearch;
  }), [opFilter, search]);

  const fOpts = HIST_FILTERS.map(f => ({
    ...f,
    count: f.value === 'all' ? MOCK_HISTORY.length : MOCK_HISTORY.filter(h => h.op.startsWith(f.value)).length,
  }));

  return (
    <div>
      <SectionTitle>Activity History</SectionTitle>
      <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
        <div style={{ flex:1, minWidth:200, maxWidth:300 }}>
          <UtilitySearchBar value={search} onChange={setSearch} placeholder="Search by operation or date..." />
        </div>
        <UtilityFilterBar options={QUICK_FILTERS} active={timeFilter} onChange={setTimeFilter} />
      </div>
      <div style={{ marginBottom:16 }}>
        <UtilityFilterBar options={fOpts} active={opFilter} onChange={setOpFilter} />
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {filtered.map(h => (
          <UtilityHistoryCard key={h.id} item={h}
            expanded={expanded === h.id}
            onToggle={() => setExpanded(expanded === h.id ? null : h.id)} />
        ))}
        {filtered.length === 0 && <UtilityEmptyState icon="🕐" title="No Activity Found" body="No history matches your current filters." />}
      </div>
    </div>
  );
}

// ── Settings ───────────────────────────────────────────────────────────────
const SETTINGS_MODULES = [
  {icon:'📁', label:'Default Output Location',  desc:'Set where processed files are saved within your VISINVOKE session.'},
  {icon:'🗜️', label:'Compression Defaults',     desc:'Set your default compression profile for new compress operations.'},
  {icon:'🗑️', label:'Recent Files Retention',   desc:'Control how long recent input files appear in your dashboard.'},
  {icon:'📂', label:'Generated Files Retention', desc:'How long output files are kept in your session activity.'},
  {icon:'📝', label:'Default Naming Rules',      desc:'Set naming patterns for automatically generated output filenames.'},
  {icon:'⚙️', label:'Processing Preferences',    desc:'Default page size, orientation, and processing quality.'},
  {icon:'🔒', label:'Privacy Controls',          desc:'File handling, session isolation, and data retention preferences.'},
  {icon:'🔔', label:'Notifications',             desc:'Receive alerts when operations complete or fail.'},
  {icon:'📤', label:'Export Configuration',      desc:'Export your PDF Toolkit settings and preferences.'},
  {icon:'📥', label:'Import Configuration',      desc:'Import a previously exported configuration.'},
  {icon:'🔧', label:'Future Integration Settings',desc:'Prepare settings for upcoming PDF Toolkit backend integration.'},
];

function Settings() {
  return (
    <div>
      <SectionTitle>Settings</SectionTitle>
      <div style={{ background:'rgba(59,130,246,0.06)', border:'1px solid rgba(59,130,246,0.2)',
        borderRadius:12, padding:'18px 22px', marginBottom:24, display:'flex', gap:14 }}>
        <span style={{ fontSize:22, flexShrink:0 }}>🔒</span>
        <div>
          <div style={{ fontSize:15, fontWeight:700, color:C.text, marginBottom:4 }}>Your Files Stay Private</div>
          <div style={{ fontSize:14, color:C.textMid, lineHeight:1.7 }}>
            Files are only used for the selected PDF operation. Generated files remain associated with your VISINVOKE session activity.
            Files are not publicly shared or stored beyond your session. Processing actions remain visible in activity history.
          </div>
        </div>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:14 }}>
        {SETTINGS_MODULES.map(s => <UtilitySettingsCard key={s.label} {...s} />)}
      </div>
    </div>
  );
}

// ── Dashboard ──────────────────────────────────────────────────────────────
const TOOLS = [
  {id:'merge',   icon:'🔗', label:'Merge PDFs',            desc:'Combine multiple PDFs into one document',    types:'Two or more PDF files', tab:'merge'   },
  {id:'split',   icon:'✂️', label:'Split PDF',             desc:'Divide a PDF into multiple separate files',  types:'One PDF file',          tab:'split'   },
  {id:'compress',icon:'🗜️',label:'Compress PDF',          desc:'Reduce PDF file size',                       types:'One PDF file',          tab:'compress'},
  {id:'rotate',  icon:'🔄', label:'Rotate PDF',            desc:'Rotate all or selected pages',               types:'One PDF file',          tab:'rotate'  },
  {id:'extract', icon:'📑', label:'Extract Pages',         desc:'Pull specific pages into a new PDF',         types:'One PDF file',          tab:'extract' },
  {id:'convert', icon:'🖼️', label:'Convert Images to PDF', desc:'Turn images into a PDF document',           types:'JPG, PNG, WEBP, BMP',   tab:'convert' },
];

const COMING_SOON = [
  {icon:'🏷️', label:'Watermark PDF',    desc:'Add text or image watermarks to every page'},
  {icon:'↕️', label:'Rearrange Pages',  desc:'Drag and drop page reordering'},
  {icon:'🗑️',label:'Remove Pages',     desc:'Delete specific pages from a PDF'},
  {icon:'ℹ️', label:'PDF Information',  desc:'View metadata, fonts, and document properties'},
  {icon:'📦', label:'Batch Processing', desc:'Process multiple files in one operation'},
];

function Dashboard({ onSelectTool, favs, onToggleFav }) {
  const [statsView, setStatsView] = useState('month');
  const stats = statsView === 'week' ? STATS_WEEK : STATS_MONTH;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:32 }}>

      <UtilityHero title="What would you like to do?" subtitle="Select a tool to get started. All operations are simple, fast, and work entirely inside VISINVOKE." />

      {/* Favourites */}
      {favs.length > 0 && (
        <UtilityFavouriteActions favs={favs} tools={TOOLS} onSelect={onSelectTool} />
      )}

      {/* Tools grid */}
      <div>
        <SectionTitle>PDF Tools</SectionTitle>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16 }}>
          {TOOLS.map(t => (
            <UtilityActionCard key={t.id} icon={t.icon} label={t.label} desc={t.desc} types={t.types}
              isFav={favs.includes(t.id)} onToggleFav={() => onToggleFav(t.id)}
              onLaunch={() => onSelectTool(t.tab)} />
          ))}
        </div>
      </div>

      {/* Stats */}
      <div>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
          <h2 style={{ margin:0, fontSize:20, fontWeight:800, color:C.text }}>Your Activity</h2>
          <div style={{ display:'flex', gap:8 }}>
            {[{v:'week',l:'This Week'},{v:'month',l:'This Month'}].map(o => (
              <button key={o.v} onClick={() => setStatsView(o.v)}
                style={{ padding:'5px 14px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer',
                  border:`1px solid ${statsView===o.v ? C.blue : C.border}`,
                  background:statsView===o.v ? 'rgba(59,130,246,0.18)' : 'rgba(255,255,255,0.05)',
                  color:statsView===o.v ? C.blueHi : C.textMid }}>
                {o.l}
              </button>
            ))}
          </div>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:14 }}>
          <UtilityStatCard icon="📄" label="Documents Processed" value={stats.docs}  accent={C.blueHi} />
          <UtilityStatCard icon="📋" label="Pages Processed"     value={stats.pages.toLocaleString()} accent={C.text} />
          <UtilityStatCard icon="✅" label="Operations Completed" value={stats.ops}  accent={C.green} />
          <UtilityStatCard icon="💾" label="Storage Saved"       value={stats.saved} accent={C.amber} />
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
          <UtilityStatCard icon="🏆" label="Most Used Tool"     value={STATS_MOST} accent={C.blueHi} />
          <UtilityStatCard icon="📈" label="Recent Success Rate" value={STATS_RATE} accent={C.green} />
        </div>
      </div>

      {/* Recent files */}
      <div>
        <SectionTitle>Recent Files</SectionTitle>
        <UtilityRecentFilesPanel files={MOCK_RECENT_FILES} onReuse={() => {}} />
      </div>

      {/* Last output files */}
      <div>
        <SectionTitle>Recent Generated Files</SectionTitle>
        {MOCK_OUTPUTS.length > 0 ? (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {MOCK_OUTPUTS.map(f => (
              <div key={f.id} style={{ background:C.surface, border:`1px solid ${C.border}`,
                borderRadius:12, padding:'14px 20px', display:'flex', alignItems:'center', gap:14, flexWrap:'wrap' }}>
                <span style={{ fontSize:22, flexShrink:0 }}>📄</span>
                <div style={{ flex:1, minWidth:120 }}>
                  <div style={{ fontSize:15, fontWeight:700, color:C.text, marginBottom:2 }}>{f.name}</div>
                  <div style={{ fontSize:13, color:C.textFaint }}>
                    {f.op} · {f.date} · {f.size}
                    {f.saved && <span style={{ color:C.green, marginLeft:6 }}>· {f.saved} saved</span>}
                  </div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <Btn label="Download" size="sm" variant="green" icon="⬇️" onClick={() => {}} />
                  <Btn label="Reuse"    size="sm" onClick={() => {}} />
                  <Btn label="Details"  size="sm" onClick={() => {}} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <UtilityEmptyState icon="📄" title="No Generated Files Yet" body="Files you create will appear here for quick download and reuse." />
        )}
      </div>

      {/* Recent activity */}
      <div>
        <SectionTitle actionLabel="View All" onAction={() => {}}>Recent Activity</SectionTitle>
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {MOCK_HISTORY.slice(0, 3).map(h => (
            <div key={h.id} style={{ background:C.surface, border:`1px solid ${C.border}`,
              borderRadius:12, padding:'14px 20px', display:'flex', alignItems:'center', gap:14, flexWrap:'wrap' }}>
              <span style={{ fontSize:22, width:32, textAlign:'center', flexShrink:0 }}>{h.icon}</span>
              <div style={{ flex:1, minWidth:140 }}>
                <div style={{ fontSize:15, fontWeight:700, color:C.text, marginBottom:2 }}>{h.op}</div>
                <div style={{ fontSize:13, color:C.textFaint }}>{h.date} · {h.files} file{h.files>1?'s':''} · {h.pages} pages</div>
              </div>
              <span style={{ fontSize:14, fontWeight:700, color:C.text }}>{h.size}</span>
              <Btn label="Details" size="sm" onClick={() => {}} />
            </div>
          ))}
        </div>
      </div>

      {/* Coming soon */}
      <div>
        <SectionTitle>Coming Soon</SectionTitle>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
          {COMING_SOON.map(c => (
            <div key={c.label} style={{ background:'rgba(255,255,255,0.02)', border:`1px solid ${C.border}`,
              borderRadius:12, padding:'16px 18px', display:'flex', gap:12 }}>
              <span style={{ fontSize:20, flexShrink:0 }}>{c.icon}</span>
              <div>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                  <span style={{ fontSize:14, fontWeight:700, color:C.textMid }}>{c.label}</span>
                  <span style={{ fontSize:11, color:C.textFaint, border:`1px solid ${C.border}`,
                    borderRadius:4, padding:'1px 7px' }}>Soon</span>
                </div>
                <div style={{ fontSize:13, color:C.textFaint, lineHeight:1.5 }}>{c.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Tabs ───────────────────────────────────────────────────────────────────
const TABS = [
  {id:'dashboard',label:'📊  Dashboard'       },
  {id:'merge',    label:'🔗  Merge PDFs'       },
  {id:'split',    label:'✂️  Split PDF'        },
  {id:'compress', label:'🗜️  Compress PDF'     },
  {id:'rotate',   label:'🔄  Rotate PDF'       },
  {id:'extract',  label:'📑  Extract Pages'    },
  {id:'convert',  label:'🖼️  Convert Images'  },
  {id:'history',  label:'🕐  Activity History' },
  {id:'settings', label:'⚙️  Settings'         },
];

// ── Page ───────────────────────────────────────────────────────────────────
export default function PdfToolkitPage() {
  // Universal product activity. This page IS the launch surface for utility_pdf_toolkit, so mounting it
  // is the only honest evidence that the customer opened the product. Once per browser
  // session; the payload carries no identity, no timestamp and no customer content, and a
  // failure is swallowed rather than allowed to break the product.
  useEffect(() => { recordProductOpened('utility_pdf_toolkit', 'web'); }, []);

  const navigate              = useNavigate();
  const { user }              = useAuth();
  const [tab, setTab]         = useState('dashboard');
  const [processing, setProc] = useState(null);
  const [favs, setFavs]       = useState(DEFAULT_FAVS);

  const handleProcess    = useCallback(op => setProc(op), []);
  const handleDoneModal  = useCallback(() => { setProc(null); setTab('history'); }, []);
  const handleCloseModal = useCallback(() => setProc(null), []);
  const handleToggleFav  = useCallback(id => setFavs(f => f.includes(id) ? f.filter(x => x !== id) : [...f, id]), []);

  const content = () => {
    switch (tab) {
      case 'dashboard': return <Dashboard onSelectTool={setTab} favs={favs} onToggleFav={handleToggleFav} />;
      case 'merge':     return <MergePDFs    onProcess={handleProcess} />;
      case 'split':     return <SplitPDF     onProcess={handleProcess} />;
      case 'compress':  return <CompressPDF  onProcess={handleProcess} />;
      case 'rotate':    return <RotatePDF    onProcess={handleProcess} />;
      case 'extract':   return <ExtractPages onProcess={handleProcess} />;
      case 'convert':   return <ConvertImages onProcess={handleProcess} />;
      case 'history':   return <ActivityHistory />;
      case 'settings':  return <Settings />;
      default:          return <Dashboard onSelectTool={setTab} favs={favs} onToggleFav={handleToggleFav} />;
    }
  };

  return (
    <div style={{ minHeight:'100vh', background:C.bg,
      fontFamily:'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>

      {/* Header */}
      <div style={{ background:'linear-gradient(160deg, #0f172a 0%, #1a2744 60%, #1e3a8a 100%)',
        borderBottom:`1px solid ${C.borderHi}` }}>
        <div style={{ maxWidth:1400, margin:'0 auto', padding:'18px 48px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
            <button onClick={() => navigate('/dashboard/utilities')}
              style={{ background:'rgba(255,255,255,0.07)', border:`1px solid ${C.border}`,
                color:C.textMid, borderRadius:8, padding:'6px 14px', fontSize:13, fontWeight:600, cursor:'pointer' }}>
              ← Utilities
            </button>
            <span style={{ color:C.textFaint, fontSize:13 }}>›</span>
            <span style={{ color:C.textFaint, fontSize:13, fontWeight:600 }}>PDF Toolkit</span>
            <span style={{ fontSize:11, fontWeight:700, color:C.green, background:'#4ade8015',
              border:'1px solid #4ade8033', borderRadius:5, padding:'2px 8px',
              textTransform:'uppercase', letterSpacing:'0.07em' }}>Free</span>
          </div>
          <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between', gap:16, flexWrap:'wrap' }}>
            <div>
              <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:6 }}>
                <span style={{ fontSize:26 }}>📄</span>
                <h1 style={{ margin:0, fontSize:27, fontWeight:900, color:C.text, letterSpacing:'-0.4px' }}>
                  VISINVOKE PDF Toolkit
                </h1>
              </div>
              <div style={{ fontSize:15, color:'rgba(255,255,255,0.6)', fontWeight:500 }}>
                Simple document management for VISINVOKE users.
              </div>
            </div>
            {user && <div style={{ fontSize:13, color:C.textMid, fontWeight:600 }}>{user.email}</div>}
          </div>
        </div>
        <div style={{ maxWidth:1400, margin:'0 auto', padding:'0 48px',
          borderTop:`1px solid ${C.border}`, display:'flex', overflowX:'auto' }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ background:'none', border:'none',
                borderBottom:`3px solid ${tab === t.id ? C.blue : 'transparent'}`,
                color:tab === t.id ? C.text : C.textMid,
                padding:'14px 20px', fontSize:14, fontWeight:tab === t.id ? 700 : 500,
                cursor:'pointer', whiteSpace:'nowrap', flexShrink:0 }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ maxWidth:1400, margin:'0 auto', padding:'32px 48px 80px' }}>
        {content()}
      </div>

      {processing && (
        <UtilityProcessingModal operation={processing} onComplete={handleDoneModal} onClose={handleCloseModal} />
      )}
    </div>
  );
}
